import { faFileArrowUp as UploadIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import { PERMISSIONS } from '@/constants/auth.constants';
import RulesCreateDialog from '@/features/RulesCreateDialog';
import RulesDeleteDialog from '@/features/RulesDeleteDialog';
import RulesDetails from '@/features/RulesDetails';
import RulesSearch from '@/features/RulesSearch';
import RulesTable from '@/features/RulesTable';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { administrationActionsAtom } from '@/pages/Administration/Administration.store';
import {
  rulesCreateDialogOpenAtom,
  rulesDeleteDialogOpenAtom,
  useFetchSensorRules
} from '@/pages/Administration/AdministrationRules';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { rulesDetailsExpandedAtom } from './AdministrationRules.store';
import useStyles from './AdministrationRules.styles';

const AdministrationRules = () => {
  const [, setPageActions] = useAtom(administrationActionsAtom);
  const [areRulesDetailsExpanded, setAreRulesDetailsExpanded] = useAtom(
    rulesDetailsExpandedAtom
  );
  const [isRulesCreateDialogOpen, setRulesCreateDialogOpen] = useAtom(
    rulesCreateDialogOpenAtom
  );
  const [isRulesDeleteDialogOpen] = useAtom(rulesDeleteDialogOpenAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { isFetching } = useFetchSensorRules();
  const userCanUploadRule = usePermissionsCheck(
    PERMISSIONS.SENSOR_RULES_UPDATE
  );
  const { classes } = useStyles({ areRulesDetailsExpanded });

  // close rules details panel when selected organization changes
  useEffect(() => {
    setAreRulesDetailsExpanded(false);
  }, [selectedOrganization]);

  // set adminstration page actions based on user permissions
  useEffect(() => {
    setPageActions(() =>
      userCanUploadRule ? (
        <Button
          color="primary"
          variant="contained"
          startIcon={<FontAwesomeIcon icon={UploadIcon} size="xs" />}
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => setRulesCreateDialogOpen(true)}
        >
          Upload Files
        </Button>
      ) : null
    );
    return () => {
      setPageActions(null);
    };
  }, [userCanUploadRule]);

  return (
    <>
      <LoadingIndicator isLoading={isFetching} />
      <Stack className={classes.administrationRules}>
        <Box className={classes.administrationRulesWrapperBox}>
          <Paper className={classes.administrationRulesPaper} elevation={2}>
            <Box className={classes.administrationRulesSearchBox}>
              <RulesSearch />
            </Box>
            <RulesTable />
          </Paper>
        </Box>
        {areRulesDetailsExpanded && <RulesDetails />}
        {isRulesCreateDialogOpen && <RulesCreateDialog />}
        {isRulesDeleteDialogOpen && <RulesDeleteDialog />}
      </Stack>
    </>
  );
};

export default AdministrationRules;
